/**
 * Typed SDK Client Generator
 *
 * Generates a typed fetch-based client from a DomainDefinitionJSON.
 * Each resource becomes a method with proper parameter and return types.
 *
 * Two outputs:
 * 1. `generateClientCode()` — generates TypeScript source code
 * 2. `createClient()` — creates a runtime client instance (no codegen)
 */

import type { DomainDefinitionJSON, ResourceDefinition } from "../schema/domain-definition";

// ---------------------------------------------------------------------------
// Runtime client (no codegen needed)
// ---------------------------------------------------------------------------

export interface ClientConfig {
  /** Base URL, e.g., "https://api.example.com" or "" for relative */
  baseUrl: string;
  /** Optional auth token getter */
  getToken?: () => Promise<string | null>;
  /** Optional custom fetch implementation */
  fetch?: typeof globalThis.fetch;
}

export interface ClientMethod {
  (params: Record<string, string>): Promise<unknown>;
}

export interface DomainClient {
  /** Client methods indexed by resource key */
  [resourceKey: string]: ClientMethod;
}

/**
 * Interpolate URL template with parameters.
 */
function interpolateUrl(template: string, params: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_match, key) => {
    const value = params[key];
    if (value === undefined) {
      throw new Error(`Missing parameter "${key}" for URL template "${template}"`);
    }
    return encodeURIComponent(value);
  });
}

/**
 * Create a runtime client instance from a DomainDefinition.
 *
 * Returns an object with one method per resource. Each method accepts
 * a params object and returns the fetched+parsed JSON response.
 *
 * @example
 * const bible = createClient(BibleDefinition, { baseUrl: "" });
 * const summary = await bible.summary({ organizationId: "epolog" });
 */
export function createClient(
  def: DomainDefinitionJSON,
  config: ClientConfig
): DomainClient {
  const fetchFn = config.fetch ?? globalThis.fetch;
  const client: DomainClient = {};

  for (const [key, resource] of Object.entries(def.resources)) {
    client[key] = async (params: Record<string, string>) => {
      const url = config.baseUrl + interpolateUrl(resource.fetch.urlTemplate, params);

      const headers: Record<string, string> = {
        Accept: "application/json",
      };

      // Add auth header for protected resources
      if (resource.authz.visibility !== "public" && config.getToken) {
        const token = await config.getToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      }

      const response = await fetchFn(url, {
        method: resource.fetch.method,
        headers,
      });

      if (!response.ok) {
        throw new ClientError(
          `${resource.fetch.method} ${url} failed: ${response.status} ${response.statusText}`,
          response.status,
          key
        );
      }

      return response.json();
    };
  }

  return client;
}

export class ClientError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly resourceKey: string
  ) {
    super(message);
    this.name = "ClientError";
  }
}

// ---------------------------------------------------------------------------
// Code generation (TypeScript source output)
// ---------------------------------------------------------------------------

/**
 * Extract parameter names from a URL template.
 * "/api/bible/{organizationId}/summary" → ["organizationId"]
 */
function extractParamNames(urlTemplate: string): string[] {
  const names: string[] = [];
  const regex = /\{(\w+)\}/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(urlTemplate)) !== null) {
    const [, name] = match;
    if (name !== undefined) names.push(name);
  }
  return names;
}

/**
 * Map ArkType expression to a TypeScript type string for codegen.
 */
function arkTypeToTsType(expr: unknown): string {
  if (typeof expr === "string") {
    const trimmed = expr.trim();

    // Array: "string[]", "TypeName[]"
    if (trimmed.endsWith("[]")) {
      const itemType = trimmed.slice(0, -2);
      return `${arkTypeToTsType(itemType)}[]`;
    }

    // Nullable: "string | null"
    if (trimmed.includes("| null")) {
      const base = trimmed.replace(/\|\s*null/, "").trim();
      return `${arkTypeToTsType(base)} | null`;
    }

    // String enum: "'draft' | 'refined' | 'production'"
    if (trimmed.includes("'") && trimmed.includes("|")) {
      return trimmed; // Already valid TS literal union
    }

    // Constrained string: "string > 0" → string
    if (trimmed.startsWith("string") && trimmed.includes(">")) {
      return "string";
    }

    // UUID: "string.uuid" → string
    if (trimmed === "string.uuid") {
      return "string";
    }

    // Primitives
    if (["string", "number", "boolean", "integer"].includes(trimmed)) {
      return trimmed === "integer" ? "number" : trimmed;
    }

    // Type reference
    return trimmed;
  }

  // Should not happen for codegen (objects are interfaces)
  return "unknown";
}

/**
 * Generate TypeScript interface source from an object type definition.
 */
function generateInterface(name: string, typeDef: Record<string, unknown>): string {
  const lines: string[] = [];
  lines.push(`export interface ${name} {`);

  for (const [key, value] of Object.entries(typeDef)) {
    if (key === "+") continue;
    const isOptional = key.endsWith("?");
    const cleanKey = isOptional ? key.slice(0, -1) : key;
    lines.push(`  ${cleanKey}${isOptional ? "?" : ""}: ${arkTypeToTsType(value)};`);
  }

  lines.push("}");
  return lines.join("\n");
}

/**
 * Generate method signature for a resource.
 */
function generateMethod(
  key: string,
  resource: ResourceDefinition,
  _def: DomainDefinitionJSON
): string {
  const paramNames = extractParamNames(resource.fetch.urlTemplate);
  const paramsType =
    paramNames.length > 0
      ? `{ ${paramNames.map((p) => `${p}: string`).join("; ")} }`
      : "Record<string, never>";

  return `  ${key}(params: ${paramsType}): Promise<${resource.type}>;`;
}

/**
 * Generate full TypeScript client source code from a DomainDefinition.
 *
 * Produces:
 * - All type interfaces
 * - A typed client interface
 * - A createXxxClient() factory function
 */
export function generateClientCode(def: DomainDefinitionJSON): string {
  const lines: string[] = [];
  const clientName = `${def.name}Client`;
  const factoryName = `create${def.name}Client`;

  lines.push(`/**`);
  lines.push(` * Auto-generated typed client for ${def.name} API v${def.version}`);
  lines.push(` * Generated from DomainDefinition — do not edit manually.`);
  lines.push(` */`);
  lines.push("");

  // Generate interfaces for object types
  for (const [name, typeDef] of Object.entries(def.types)) {
    if (typeof typeDef === "object" && typeDef !== null && !Array.isArray(typeDef)) {
      lines.push(generateInterface(name, typeDef as Record<string, unknown>));
      lines.push("");
    }
  }

  // Generate client interface
  lines.push(`export interface ${clientName} {`);
  for (const [key, resource] of Object.entries(def.resources)) {
    lines.push(generateMethod(key, resource, def));
  }
  lines.push("}");
  lines.push("");

  // Generate factory
  lines.push(`export interface ${clientName}Config {`);
  lines.push("  baseUrl: string;");
  lines.push("  getToken?: () => Promise<string | null>;");
  lines.push("}");
  lines.push("");

  lines.push(`export function ${factoryName}(config: ${clientName}Config): ${clientName} {`);
  lines.push("  const { baseUrl, getToken } = config;");
  lines.push("");
  lines.push("  async function request(method: string, url: string, isProtected: boolean) {");
  lines.push('    const headers: Record<string, string> = { Accept: "application/json" };');
  lines.push("    if (isProtected && getToken) {");
  lines.push("      const token = await getToken();");
  lines.push('      if (token) headers.Authorization = `Bearer ${token}`;');
  lines.push("    }");
  lines.push("    const response = await fetch(url, { method, headers });");
  lines.push("    if (!response.ok) throw new Error(`${method} ${url}: ${response.status}`);");
  lines.push("    return response.json();");
  lines.push("  }");
  lines.push("");
  lines.push("  return {");

  for (const [key, resource] of Object.entries(def.resources)) {
    const paramNames = extractParamNames(resource.fetch.urlTemplate);
    const isProtected = resource.authz.visibility !== "public";

    // Build URL interpolation
    let urlExpr = `\`\${baseUrl}${resource.fetch.urlTemplate}\``;
    for (const param of paramNames) {
      urlExpr = urlExpr.replace(
        `{${param}}`,
        `\${encodeURIComponent(params.${param})}`
      );
    }

    lines.push(`    async ${key}(params) {`);
    lines.push(`      return request("${resource.fetch.method}", ${urlExpr}, ${isProtected}) as Promise<${resource.type}>;`);
    lines.push("    },");
  }

  lines.push("  };");
  lines.push("}");

  return lines.join("\n");
}
